import React, { useRef, useMemo } from "react";
import { PolarArea } from "react-chartjs-2";
import { Chart, RadialLinearScale, ArcElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";

Chart.register(RadialLinearScale, ArcElement, Tooltip, Legend, ChartDataLabels);

const fallbackColors = [
    "#aeea84", "#fcd39e", "#a8a1e9", "#ff6384", "#36a2eb", "#ffce56", "#b8e994",
    "#f6e58d", "#686de0", "#303952", "#f5cd79", "#574b90", "#f3a683", "#574b90"
];

const truncateLabel = (str, maxLen = 32) => {
    if (typeof str !== "string") return "";
    return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
};

const whiteBackgroundPlugin = {
    id: 'custom_canvas_background_color',
    beforeDraw: (chart) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    }
};

function downloadAllChartsAsPng(containerRef, title = "all_charts") {
    if (!containerRef.current) return;
    html2canvas(containerRef.current, {
        backgroundColor: "#fff",
        scale: 4,
        useCORS: true,
        allowTaint: true
    }).then(canvas => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${title.replace(/\s+/g, "_")}.png`;
        link.click();
    });
}

function ResearchPipelinePolarCharts({
    codes = [],
    files = [],
    titlePrefix = "Interview",
    title = ""
}) {
    const containerRef = useRef();

    // Build stable color map by code uuid (backend now sends UUIDs)
    const codeColorMap = useMemo(() => {
        const map = {};
        if (!Array.isArray(codes)) {
            return map;
        }
        
        try {
            codes.forEach((code, idx) => {
                const codeId = code?.uuid || code?.id; // Support both for backwards compatibility
                if (codeId) {
                    map[codeId] = code?.color || fallbackColors[idx % fallbackColors.length];
                }
            });
        } catch (error) {
            console.error('Error building color map:', error);
        }
        return map;
    }, [codes]);

    // Filter out files that have no excerpts
    const filesWithExcerpts = useMemo(() => {
        if (!Array.isArray(files) || !Array.isArray(codes)) {
            return [];
        }
        
        try {
            return files.filter(file => {
                // Check if any code has excerpts for this file
                return codes.some(code =>
                    Array.isArray(code?.excerpts) &&
                    code.excerpts.some(excerpt => excerpt?.analysis_uuid === file?.uuid)
                );
            });
        } catch (error) {
            console.error('Error filtering files with excerpts:', error);
            return [];
        }
    }, [files, codes]);

    // Prepare legend data using the map
    const legendData = useMemo(() => {
        if (!Array.isArray(codes)) {
            return [];
        }
        
        try {
            return codes
                .map(code => {
                    const codeId = code?.uuid || code?.id; // Support both for backwards compatibility
                    return {
                        name: truncateLabel(code?.name || 'Unknown', 38),
                        color: codeColorMap[codeId] || '#gray'
                    };
                })
                .filter((entry, idx, arr) =>
                    entry.name && arr.findIndex(e => e.name === entry.name) === idx
                );
        } catch (error) {
            console.error('Error preparing legend data:', error);
            return [];
        }
    }, [codes, codeColorMap]);

    // Show message if no interviews have excerpts
    if (filesWithExcerpts.length === 0) {
        return (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                <div className="flex justify-center items-center min-h-fit bg-white">
                    <div className="w-full max-w-7xl">
                        <div className="border border-gray-300 shadow p-8 bg-white w-full">
                            <div className="text-gray-500 italic text-center py-8">
                                No interviews contain excerpts to display.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            <div className="flex justify-center items-center min-h-fit bg-white">
                <div className="w-full max-w-7xl">
                    <div className="border border-gray-300 shadow p-8 bg-white w-full">
                        <div ref={containerRef}>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 justify-items-center items-center">
                                {filesWithExcerpts.map((file, i) => (
                                    <SingleInterviewPolarAreaChart
                                        key={file.uuid || file.name || i}
                                        file={file}
                                        codes={codes}
                                        codeColorMap={codeColorMap}
                                        title={`${file.name || file.uuid || `Interview ${i + 1}`}`}
                                    />
                                ))}
                            </div>
                            {/* Unified legend below charts */}
                            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 mt-10 w-full">
                                {legendData.map((item, idx) => (
                                    <svg
                                        key={idx}
                                        width="250"
                                        height="24"
                                        style={{ display: "block", marginBottom: 8 }}
                                    >
                                        <rect x="0" y="3" width="18" height="18" fill={item.color} stroke="#bbb" rx="4" />
                                        <text
                                            x="26"
                                            y="14"
                                            fontFamily="Inter, Arial, Helvetica, sans-serif"
                                            fontSize="16"
                                            fill="#444"
                                            alignmentBaseline="middle"
                                        >
                                            {item.name}
                                        </text>
                                    </svg>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SingleInterviewPolarAreaChart({ codes, file, title, codeColorMap }) {
    const chartRef = useRef();

    // Only codes with excerpts in THIS interview (file)
    const { labels, values, colors } = useMemo(() => {
        if (!Array.isArray(codes) || !file?.uuid || !codeColorMap) {
            return { labels: [], values: [], colors: [] };
        }
        
        try {
            const filtered = codes.map((code) => {
                const count = Array.isArray(code?.excerpts)
                    ? code.excerpts.filter(
                        (ex) => ex?.analysis_uuid === file.uuid
                    ).length
                    : 0;
                const codeId = code?.uuid || code?.id; // Support both for backwards compatibility
                return count > 0
                    ? {
                        label: truncateLabel(code?.name || 'Unknown', 38),
                        value: count,
                        color: codeColorMap[codeId] || '#gray'
                    }
                    : null;
            }).filter(Boolean);

            return {
                labels: filtered.map(c => c.label),
                values: filtered.map(c => c.value),
                colors: filtered.map(c => c.color),
            };
        } catch (error) {
            console.error('Error processing interview chart data:', error);
            return { labels: [], values: [], colors: [] };
        }
    }, [codes, file, codeColorMap]);

    const data = {
        labels,
        datasets: [{
            label: "",
            data: values,
            backgroundColor: colors
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1, // Always square
        layout: {
            padding: {
                top: 60,
                bottom: 60,
                left: 50,
                right: 50
            }
        },
        scales: {
            r: {
                pointLabels: {
                    display: false,
                    centerPointLabels: true,
                    font: { size: 12 },
                    padding: 10,
                },
                ticks: { display: false },
                beginAtZero: true
            }
        },
        devicePixelRatio: 3,
        plugins: {
            legend: { display: false }, // Hide Chart.js legend
            title: { display: false },
            datalabels: {
                display: true,
                color: "#000",
                font: { weight: "thin", size: 14 },
                formatter: (v) => (v > 0 ? v : ""),
                anchor: "middle",
                align: "middle",
                offset: 0,
            }
        }
    };

    return (
        <div className="flex flex-col items-center w-full max-w-[420px] mx-auto min-h-[450px]">
            <div className="text-xl font-semibold mb-4 text-center w-full">{title}</div>
            <PolarArea
                ref={chartRef}
                data={data}
                options={options}
                plugins={[whiteBackgroundPlugin]}
            />
        </div>
    );
}

export default ResearchPipelinePolarCharts;